import { apiRequest, ApiError } from '@/lib/api/client';
import { withResolvedAvatarUrls } from '@/lib/api/avatar-url';
import { getAuthToken } from '@/lib/api/token-store';

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || '').trim();
  if (!normalizedToken) {
    throw new ApiError('authorization token is required', 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/** Server-side minimum; a shorter query matches nearly everything and is never worth a request. */
export const MESSAGE_SEARCH_MIN_TERM_LENGTH = 2;

/**
 * Search message text.
 * GET /api/threads/messages:search?q=&threadIds=&scope=&fromCreatedAt=&toCreatedAt=&limit=&offset=&withTotal=
 *
 * `threadIds` scopes the search to specific conversations: the open thread, or — when the merged
 * WhatsApp view is on — every per-clinic sibling thread the stream is showing, since a "merged
 * conversation" is N threads client-side and the server has no such concept. Omitting it searches
 * every conversation the caller is a member of, within one partition (`scope`).
 *
 * `withTotal` asks for the exact match count. Only worth setting for the scoped case: an all-chats
 * count has to walk every match, so that mode pages with offset instead.
 *
 * Returns { results, threads, page }. `threads` is one ENRICHED thread row per conversation the
 * page touched (same shape as GET /api/threads rows) — result rows carry only ids, so the caller
 * joins on threadId for titles, avatars, patientUserId and whatsappBotId.
 */
export async function searchMessages({
  token,
  q,
  threadIds,
  scope,
  fromCreatedAt,
  toCreatedAt,
  limit,
  offset,
  withTotal,
} = {}) {
  const term = String(q || '').trim();
  if (term.length < MESSAGE_SEARCH_MIN_TERM_LENGTH) {
    throw new ApiError(`q must be at least ${MESSAGE_SEARCH_MIN_TERM_LENGTH} characters`, 400);
  }

  const params = new URLSearchParams();
  params.set('q', term);

  const normalizedThreadIds = (Array.isArray(threadIds) ? threadIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  if (normalizedThreadIds.length) params.set('threadIds', normalizedThreadIds.join(','));

  const normalizedScope = String(scope || '').trim();
  if (normalizedScope) params.set('scope', normalizedScope);

  const normalizedFrom = String(fromCreatedAt || '').trim();
  if (normalizedFrom) params.set('fromCreatedAt', normalizedFrom);
  const normalizedTo = String(toCreatedAt || '').trim();
  if (normalizedTo) params.set('toCreatedAt', normalizedTo);

  if (Number.isFinite(limit) && limit > 0) params.set('limit', String(Math.floor(limit)));
  if (Number.isFinite(offset) && offset > 0) params.set('offset', String(Math.floor(offset)));
  if (withTotal) params.set('withTotal', '1');

  const payload = await apiRequest(`/api/threads/messages:search?${params.toString()}`, {
    method: 'GET',
    headers: createAuthHeaders(token),
  });

  const results = Array.isArray(payload?.results) ? payload.results : [];
  const rawPage = payload?.page && typeof payload.page === 'object' ? payload.page : {};
  return {
    results: results.map((row) => ({
      messageId: String(row?.messageId || '').trim(),
      threadId: String(row?.threadId || '').trim(),
      createdAt: row?.createdAt || null,
      contentType: String(row?.contentType || '').trim(),
      sender: row?.sender && typeof row.sender === 'object' ? row.sender : null,
      snippet: String(row?.snippet || ''),
      matchOffset: Number.isFinite(row?.matchOffset) ? row.matchOffset : 0,
    })),
    threads: withResolvedAvatarUrls(payload?.threads),
    page: {
      limit: Number.isFinite(rawPage.limit) ? rawPage.limit : (limit ?? 0),
      offset: Number.isFinite(rawPage.offset) ? rawPage.offset : (offset ?? 0),
      returned: Number.isFinite(rawPage.returned) ? rawPage.returned : results.length,
      // null (not 0) when the caller did not ask for a total — "unknown", not "none".
      total: Number.isFinite(rawPage.total) ? rawPage.total : null,
      // The conversation scope was clipped at the server cap, so recall is partial.
      truncated: Boolean(rawPage.truncated),
    },
    raw: payload,
  };
}
